import { Component } from '@angular/core';
import { Authenticated, AuthenticationService, CurrentUser, Option, HumanResources, AuthenticatedIdentity, Academic, OperationsService, DossierService, AcademicTitleId, AuthResultImpl, UserService } from '../../domain';
import { Router } from '@angular/router';
import { ConsoleService } from '../../widgets/console.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { LOCAL_STORAGE_PB_USER_CLAIMS } from 'app/shared/constants';

@Component({
	selector: 'user-menu',
	templateUrl: './user-menu.component.html',
	styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent {
	HR = HumanResources;
	currentUser: Option<CurrentUser> = Option.create<CurrentUser>();
	navLinks: any[] = [];
	loginReportAccess:string[]=[
		"10001223" /*Christopher Hanson*/,
		"10001885" /*Kit Alviz*/,
		"10249725" /*Anna Lee*/,
		"10604634" /*Uriel Gonzalez*/,
		"10821231" /*Gary Sundquist*/,
		"10786960" /*Chris Dolan*/
	];
	isAcademic: boolean = false;
	isCampusMPAcademic: boolean = false;
	isStaffDirector: boolean = false;
	menuOpen: boolean = false;
	showUsageDownloadButton = false;
	showReadModelResetButton = false;
	showCommandButton = false;
	showLoginDownloadButton = false;
	constructor(
		private authSvc: AuthenticationService,
		private router: Router,
		private operationsSvc: OperationsService,
		private console: ConsoleService,
		private dossierSvc: DossierService,
		private gaService: GoogleAnalyticsService,
		private snackbar: MatSnackBar,
		private userService: UserService
	) {

		authSvc.currentIdentity.subscribe(identity => {

			const claimsJson = window.localStorage.getItem(LOCAL_STORAGE_PB_USER_CLAIMS);
    		let authResult = claimsJson ? AuthResultImpl.fromJson(JSON.parse(claimsJson)) : null;
			let employeeId = authResult?.employeeId ?? "";
			
			identity.match(
				() => {
					this.currentUser = Option.create<CurrentUser>();
					this.navLinks = [];
				},
				(i: AuthenticatedIdentity) => {
					this.currentUser = Option.create<CurrentUser>(i.user);
					this.showUsageDownloadButton = i.user.roles.programPlanningAndEvaluation || i.user.roles.academicHumanResources;
					this.showReadModelResetButton = i.user.roles.sysAdmin;
					this.showCommandButton = i.user.roles.sysAdmin;
					this.showLoginDownloadButton = this.loginReportAccess.indexOf(employeeId) !== -1;
					this.isCampusMPAcademic = i.user.roles.academic.map(a => a.meritAndPromotionHr === HumanResources.Campus).getValueOrDefault(false);
					this.isStaffDirector = i.user.roles.academic.map(a => a.title.id == AcademicTitleId.StaffDirector).getValueOrDefault(false);
					this.initNavLinks(i.user);
				});
		});
	}

	excelExport(){
		this.snackbar.open('Preparing download...');
		this.dossierSvc.downloadExcel().then(r => r.matchDo(
			_ => {
				this.snackbar.open('FTE Export is downloading');
				this.gaService.eventEmitter("FTE Export Downloaded", "FTE Export", "Download");
			},
			errorMessage => this.snackbar.open(errorMessage)));
	}

	wordExport(){
		this.snackbar.open('Preparing download...');
		this.dossierSvc.downloadThemesAndProjectsDoc().then(r => r.matchDo(
			_ => {
				this.snackbar.open('Themes and Projects Export is downloading');
				this.gaService.eventEmitter("Themes and Projects Export Downloaded", "Themes and Projects Export", "Download");
			},
			errorMessage => this.snackbar.open(errorMessage)));
	}

	resetReadModels(){
		this.operationsSvc.resetReadModels();
	}

	askForCommand(){
		var id = prompt('Enter Academic Aggregate ID');
		if(!id){
			return;
		}
		var json = prompt('Enter COMMAND for aggregate ' + id);
		if(!json){
			return;
		}
		let cmd = JSON.parse(json);
		this.operationsSvc.executeCommand(id,cmd).then(result => result.matchDo(_ => alert('OK'), error => alert(error)));
	}
	public downloadUserLoginReport(){
		this.operationsSvc.downloadUserLoginReportExcel();
	}	
	downloadUsageReport(){
		this.operationsSvc.downloadUsageReport();
		this.gaService.eventEmitter("Usage Report Downloaded", "Usage Report", "Download");
	}

	unproxy(){
		this.userService.unspoofUser().then(result => {
			const authenticated = Authenticated.fromJson(result);
			this.authSvc.accept(authenticated);
			window.location.reload();
		}, error => {
			this.snackbar.open('Unable to end proxy: ' + error?.message);
		});
	}

	logOut(){
		this.gaService.eventEmitter("Logout", "Logout", "Success");
		this.authSvc.clearCurrentUser();
		this.router.navigateByUrl('/');
	}

	urlActive(link: { url: string, activeWhen?: (current: string) => boolean }) {
		const current = this.router.url.split('?')[0];
		if (link.activeWhen) {
			return link.activeWhen(current);
		}
		return current.split('/')[1] == link.url.substring(1, link.url.length);
	}

	private initNavLinks(cu: CurrentUser){
		let isAcademic = this.isAcademic = cu.roles.academic.hasValue();

		this.navLinks = [
			{ 
				url: '/ahr',
				label: 'Academic HR',
				iconClass: '',
				visible: cu.roles.academicHumanResources 
			},
			/*
			{ 
				url: '/ahr_old',
				label: 'Academic HR (old)',
				iconClass: '',
				visible: cu.roles.academicHumanResources 
			},
			*/
			{
				url: '/crc',
				label: 'Civil Rights Compliance',
				iconClass: '',
				visible: cu.roles.civilRightsCompliance
			},
			{
				url: '/academic',
				label: 'Project Board',
				iconClass: '',
				visible: isAcademic || cu.roles.academicHumanResources
			},
			{
				url: '/review',
				// '/review' is a shared prefix of every dashboard route, so first-segment
				// matching can't tell the landing page apart from the reviewer tabs. Both
				// review links carry an explicit activeWhen so each lights up for its own
				// destination only: the landing here, everything else under the dashboard.
				activeWhen: (current: string) => current === '/review' || current.startsWith('/review/home'),
				label: 'Academic Program Review',
				iconClass: 'fa fa-briefcase',
				visible: cu.canAccessReview && cu.roles.academic.match(
					() => false,
					(a: Academic) => a.meritAndPromotionHr == HumanResources.ANR && !this.isStaffDirector)
			},
			{
				url: '/review/my-review',
				activeWhen: (current: string) =>
					current.startsWith('/review') && current !== '/review' && !current.startsWith('/review/home'),
				label: 'Reviewer Dashboard',
				iconClass: 'fa fa-comments',
				visible: cu.canAccessReview && cu.roles.academic.match(
					() => false,
					(a: Academic) => a.meritAndPromotionHr == HumanResources.ANR)
			},
			{
				url: '/admin-proxy',
				label: 'Admin Proxy',
				iconClass: '',
				// Not proxied: cu is the logged-in user, so show if they are a PPE admin.
				// Proxied: cu is the spoofed user, but only a PPE admin can start a proxy, so always show.
				visible: cu.roles.programPlanningAndEvaluation || cu.isProxied
			},
		].filter(l => l.visible);
	}
}